import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Icon,
  Stack,
  Switch,
  ListSubheader,
  IconButton,
  Tooltip,
  Typography
} from 'investira.react.components';
import { BaseTemplate } from '../../components/templates';
import app from '../../utils/app';
import { PATHS } from '../../enums';
import { useEntidadesContext } from '../../contexts/EntidadesContext';
import usePrevious from '../../hooks/usePrevious';
import usePerfil from '../../hooks/usePerfil';

function Configuracoes() {
  let { pathnameBase } = useMatch('/configuracoes/*');
  const { state, actions } = useEntidadesContext();
  const { current, data } = useSelector(store => store.entidade);
  const navigate = useNavigate();

  const { usuarioPrioridade } = usePerfil();

  const { config } = data.find(xEntidade => xEntidade.entidade_id === current.default_entidade_id);

  const [custodiante, setCustodiante] = useState(false);
  const [acesso, setAcesso] = useState(false);
  const [assinatura, setAssinatura] = useState(false);
  const [feederMaster, setFeederMaster] = useState(false);
  const [aberturaPosicaoRecente, setAberturaPosicaoRecente] = useState(false);

  useEffect(() => {
    setCustodiante(config?.custodiante_conferencia || false);
    setAcesso(config?.acesso || false);
    setAssinatura(config?.assinatura || false);
    setFeederMaster(config?.ifeed_feeder || false);
    setAberturaPosicaoRecente(config?.abertura_posicao_recente || false);
  }, [config]);

  const prevCustodiante = usePrevious(custodiante || false);
  const prevAcesso = usePrevious(acesso || false);
  const prevAssinatura = usePrevious(assinatura || false);
  const prevAberturaPosicaoRecente = usePrevious(aberturaPosicaoRecente || false);

  function updateEntidadeAndRefresh(pConfig) {
    actions.handleModifyEntidade(
      {
        data: {
          config: pConfig
        }
      },
      () => {
        actions.readEntidades();
        actions.readMembros();
      }
    );
  }

  useEffect(() => {
    if (prevCustodiante !== custodiante) {
      updateEntidadeAndRefresh({ custodiante_conferencia: custodiante });
    }
  }, [prevCustodiante, custodiante]);

  useEffect(() => {
    if (prevAcesso !== acesso) {
      updateEntidadeAndRefresh({ acesso: acesso });
    }
  }, [prevAcesso, acesso]);

  useEffect(() => {
    if (prevAssinatura !== assinatura) {
      updateEntidadeAndRefresh({ assinatura: assinatura });
    }
  }, [prevAssinatura, assinatura]);

  useEffect(() => {
    if (prevAberturaPosicaoRecente !== aberturaPosicaoRecente) {
      updateEntidadeAndRefresh({ abertura_posicao_recente: aberturaPosicaoRecente });
    }
  }, [prevAberturaPosicaoRecente, aberturaPosicaoRecente]);

  return (
    <BaseTemplate title="Configurações" maxWidth="md">
      <Stack spacing={2}>
        <List component={'nav'} subheader={<ListSubheader>Segurança</ListSubheader>}>
          <ListItem
            component={Link}
            to={pathnameBase + PATHS.configuracoes.nested['alterar-senha'].path}>
            <ListItemIcon>
              <Icon iconName={'key'} size={21} color={'greenLight'} />
            </ListItemIcon>
            <ListItemText
              primary="Alterar Senha"
              primaryTypographyProps={{
                color: 'textPrimary'
              }}
            />
          </ListItem>
        </List>

        <List component={'nav'} subheader={<ListSubheader>Entidades</ListSubheader>}>
          <ListItem component={Link} to={pathnameBase + PATHS.configuracoes.nested.entidades.path}>
            <ListItemIcon>
              <Icon iconName={'parthenon'} size={21} color={'greenLight'} />
            </ListItemIcon>
            <ListItemText
              primary="Minhas Entidades"
              primaryTypographyProps={{
                color: 'textPrimary'
              }}
            />
          </ListItem>
          <ListItem
            component={Link}
            to={pathnameBase + PATHS.configuracoes.nested.entidades.nested.membros.path}>
            <ListItemIcon>
              <Icon iconName={'users'} size={21} color={'greenLight'} />
            </ListItemIcon>
            <ListItemText
              primary="Membros"
              primaryTypographyProps={{
                color: 'textPrimary'
              }}
            />
          </ListItem>

          <ListItem disabled={usuarioPrioridade > 2}>
            <ListItemIcon>
              <Icon iconName={'toll'} size={21} color={'greenLight'} />
            </ListItemIcon>
            <ListItemText
              primary="Controle de acesso"
              primaryTypographyProps={{
                color: 'textPrimary'
              }}
              secondary="Permissões e acessos"
            />
            <Switch
              edge="end"
              onChange={() => setAcesso(prevState => !prevState)}
              checked={acesso}
              disabled={usuarioPrioridade > 2}
            />
          </ListItem>

          <ListItem disabled={usuarioPrioridade > 2 || !config?.acesso}>
            <ListItemIcon>
              <Icon iconName={'stamp'} size={21} color={'greenLight'} />
            </ListItemIcon>
            <ListItemText
              primary="Controle de Assinatura"
              primaryTypographyProps={{
                color: 'textPrimary'
              }}
            />
            <Switch
              edge="end"
              onChange={() => setAssinatura(prevState => !prevState)}
              checked={assinatura}
              disabled={usuarioPrioridade > 2 || !config?.acesso}
            />
          </ListItem>

          <ListItem disabled={usuarioPrioridade > 2}>
            <ListItemIcon>
              <Icon iconName={'policeman'} size={21} color={'greenLight'} />
            </ListItemIcon>
            <ListItemText
              primary="Controle de Conferência com Custodiante"
              primaryTypographyProps={{
                color: 'textPrimary'
              }}
            />
            <Switch
              edge="end"
              onChange={() => setCustodiante(prevState => !prevState)}
              checked={custodiante}
              disabled={usuarioPrioridade > 2}
            />
          </ListItem>

          <ListItem
            component={usuarioPrioridade > 2 ? 'span' : Link}
            to={pathnameBase + PATHS.configuracoes.nested.entidades.nested['alerta-usuarios'].path}
            disabled={usuarioPrioridade > 2}>
            <ListItemIcon>
              <Icon iconName={'bell'} size={21} color={'greenLight'} />
            </ListItemIcon>
            <ListItemText
              primary="Controle de Notificações de Usuários"
              primaryTypographyProps={{
                color: 'textPrimary'
              }}
            />
          </ListItem>
        </List>

        <List component={'nav'} subheader={<ListSubheader>Relatórios</ListSubheader>}>
          <ListItem
            component={usuarioPrioridade > 2 ? 'span' : Link}
            to={pathnameBase + PATHS.configuracoes.nested['benchmarks'].path}
            disabled={usuarioPrioridade > 2}>
            <ListItemIcon>
              <Icon iconName={'chart_line'} size={21} color={'greenLight'} />
            </ListItemIcon>
            <ListItemText
              primary="Benchmarks"
              primaryTypographyProps={{
                color: 'textPrimary'
              }}
            />
          </ListItem>

          <ListItem
            component={!feederMaster || usuarioPrioridade > 2 ? 'span' : Link}
            to={pathnameBase + PATHS.configuracoes.nested['feeder-master'].path}
            disabled={!feederMaster || usuarioPrioridade > 2}>
            <ListItemIcon>
              <Icon iconName={'dna'} size={21} color={'greenLight'} />
            </ListItemIcon>
            <ListItemText
              primary="Feeder e Master"
              primaryTypographyProps={{
                color: 'textPrimary'
              }}
            />
          </ListItem>
        </List>
        <List component={'nav'} subheader={<ListSubheader>Abertura de Fundos</ListSubheader>}>
          <ListItem
            component={usuarioPrioridade > 2 ? 'span' : Link}
            to={pathnameBase + PATHS.configuracoes.nested['fundos-fechados'].path}
            disabled={usuarioPrioridade > 2}>
            <ListItemIcon>
              <Icon iconName={'fund'} size={21} color={'greenLight'} />
            </ListItemIcon>
            <ListItemText
              primary="Fundos Excepcionalmente Não Abertos"
              primaryTypographyProps={{
                color: 'textPrimary'
              }}
            />
          </ListItem>
          <ListItem disabled={usuarioPrioridade > 2}>
            <ListItemIcon>
              <Icon iconName={'box_3d_open'} size={21} color={'greenLight'} />
            </ListItemIcon>
            <ListItemText
              //primary="Na ausência de posição, na data selecionada, dos fundos cuja carteira será detalhada (posição aberta), será considerada a posição mais recente disponível"
              primary={
                <Stack direction="row" alignItems="center" gap={1}>
                  Utilizar a data de posição mais recente disponível para o fundo
                  <Tooltip
                    title={
                      <Typography color="inherit" variant="caption" component="p" gutterBottom>
                        Na ausência de posição, na data selecionada, dos fundos cuja carteira será
                        detalhada (posição aberta), será considerada a posição mais recente
                        disponível.
                      </Typography>
                    }>
                    <span style={{ marginTop: '2px' }}>
                      <Icon size={16} color={'primary'} iconName="information" />
                    </span>
                  </Tooltip>
                </Stack>
              }
              primaryTypographyProps={{
                color: 'textPrimary'
              }}
            />
            <Switch
              edge="end"
              onChange={() => setAberturaPosicaoRecente(prevState => !prevState)}
              checked={aberturaPosicaoRecente}
              disabled={usuarioPrioridade > 2}
            />
          </ListItem>
        </List>

        <List
          component={'nav'}
          subheader={<ListSubheader>Conferência com Custodiante</ListSubheader>}>
          <ListItem
            component={!custodiante || usuarioPrioridade > 4 ? 'span' : Link}
            to={
              pathnameBase +
              PATHS.configuracoes.nested.entidades.nested['custodiante-ativos-ignorados'].path
            }
            disabled={!custodiante || usuarioPrioridade > 4}>
            <ListItemIcon>
              <Icon iconName="box_3d" size={21} color={'greenLight'} />
            </ListItemIcon>
            <ListItemText
              primary="Ativos Ignorados na Conferência"
              primaryTypographyProps={{
                color: 'textPrimary'
              }}
            />
          </ListItem>

          <ListItem
            component={!custodiante || usuarioPrioridade > 4 ? 'span' : Link}
            to={
              pathnameBase + PATHS.configuracoes.nested.entidades.nested['custodiante-config'].path
            }
            disabled={!custodiante || usuarioPrioridade > 4}>
            <ListItemIcon>
              <Icon iconName="gear" size={21} color={'greenLight'} />
            </ListItemIcon>
            <ListItemText
              primary="Configurações da Conferência"
              primaryTypographyProps={{
                color: 'textPrimary'
              }}
            />
          </ListItem>
        </List>
        <List component={'nav'} subheader={<ListSubheader>Sessão</ListSubheader>}>
          <ListItem
            button
            onClick={() => {
              app.logout();
            }}>
            <ListItemIcon>
              <Icon iconName={'on_off'} size={21} color={'greenLight'} />
            </ListItemIcon>
            <ListItemText
              primary="Sair"
              primaryTypographyProps={{
                color: 'textPrimary'
              }}
            />
          </ListItem>
        </List>
      </Stack>
    </BaseTemplate>
  );
}

Configuracoes.displayName = 'Configuracoes';

export default Configuracoes;
